import { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  Alert,
  CircularProgress,
  Snackbar,
  IconButton,
  Link,
  InputAdornment,
} from '@mui/material';
import { motion } from 'framer-motion';
import SendIcon from '@mui/icons-material/Send';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SubjectIcon from '@mui/icons-material/Subject';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectContactInfo,
  submitContactMessage,
  resetSubmitStatus,
  selectSubmitStatus,
  selectSubmitError
} from '../redux/contactSlice';
import PageHeader from '../components/PageHeader';

function Contact() {
  const dispatch = useDispatch();
  const contactInfo = useSelector(selectContactInfo);
  const submitStatus = useSelector(selectSubmitStatus);
  const submitError = useSelector(selectSubmitError);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // UX: input limits and button state
  const SUBJECT_MAX = 120;
  const MESSAGE_MAX = 1000;
  const loading = submitStatus === 'loading';
  const isDisabled =
    loading ||
    !formData.name.trim() ||
    !formData.email.trim() ||
    !formData.subject.trim() ||
    !formData.message.trim();

  useEffect(() => {
    if (submitStatus === 'succeeded') {
      setSnackbar({
        open: true,
        message: 'Thank you for your message! We will get back to you soon.',
        severity: 'success',
      });
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
      });
    } else if (submitStatus === 'failed') {
      setSnackbar({
        open: true,
        message: submitError || 'Something went wrong. Please try again.',
        severity: 'error',
      });
    }
  }, [submitStatus, submitError]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    dispatch(submitContactMessage(formData));
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
    if (submitStatus !== 'idle') {
      dispatch(resetSubmitStatus());
    }
  };


  return (
    <Box>
      <PageHeader
        title="Get in Touch"
        backgroundImage="/images/headers/contact_bg.jpeg"
        subtitle="Have questions about Gymkhana? We'd love to hear from you!"
      />
      <Box sx={{
        background: 'linear-gradient(135deg, #f5f5f5 0%, #fff 100%)',
        py: 8,
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 150%, rgba(0, 31, 63, 0.03) 0%, transparent 50%),\n                     radial-gradient(circle at 80% -50%, rgba(255, 64, 129, 0.03) 0%, transparent 50%)',
          zIndex: 0,
        },
      }}>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >



            <Grid container spacing={4}>
              <Grid item xs={12} md={4}>
                <Box sx={{ mb: { xs: 4, md: 0 } }}>
                  <Paper
                    elevation={3}
                    sx={{
                      p: 3,
                      height: '100%',
                      background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.95) 100%)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255,255,255,0.3)',
                      boxShadow: '0 8px 32px rgba(0, 31, 63, 0.1)',
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 3,
                        color: 'primary.main',
                        fontFamily: 'Bebas Neue',
                        fontSize: '1.5rem',
                      }}
                    >
                      Contact Information
                    </Typography>
                    <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                      <LocationOnIcon sx={{ mr: 2, color: 'primary.main' }} />
                      <Typography variant="body1">
                        {contactInfo?.address || 'Loading address...'}
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                      <PhoneIcon sx={{ mr: 2, color: 'primary.main' }} />
                      <Typography variant="body1">{contactInfo?.phone || 'Loading phone...'}</Typography>
                    </Box>
                    <Box
                      component="a"
                      href={`mailto:${contactInfo?.email || 'ceo@ctms.org.tw'}?cc=intimation@chronopulse.com`}
                      sx={{
                        mb: 3,
                        display: 'flex',
                        alignItems: 'center',
                        textDecoration: 'none',
                        color: 'inherit',
                        transition: 'all 0.2s ease',
                        cursor: 'pointer',
                        '&:hover': {
                          color: 'primary.main',
                          '& .email-text': {
                            color: 'primary.main',
                            textDecoration: 'underline',
                          },
                          '& .MuiSvgIcon-root': {
                            transform: 'scale(1.1)',
                          },
                        },
                      }}
                    >
                      <EmailIcon sx={{ mr: 2, color: 'primary.main', transition: 'transform 0.2s ease' }} />
                      <Typography className="email-text" variant="body1" sx={{ transition: 'color 0.2s ease' }}>
                        {contactInfo?.email || 'ceo@ctms.org.tw'}
                      </Typography>
                    </Box>
                    {contactInfo?.socialMedia && (
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        {contactInfo.socialMedia.facebook && (
                          <IconButton
                            component={Link}
                            href={contactInfo.socialMedia.facebook}
                            target="_blank"
                            color="primary"
                          >
                            <FacebookIcon />
                          </IconButton>
                        )}
                        {contactInfo.socialMedia.twitter && (
                          <IconButton
                            component={Link}
                            href={contactInfo.socialMedia.twitter}
                            target="_blank"
                            color="primary"
                          >
                            <TwitterIcon />
                          </IconButton>
                        )}
                        {contactInfo.socialMedia.instagram && (
                          <IconButton
                            component={Link}
                            href={contactInfo.socialMedia.instagram}
                            target="_blank"
                            color="primary"
                          >
                            <InstagramIcon />
                          </IconButton>
                        )}
                        {contactInfo.socialMedia.youtube && (
                          <IconButton
                            component={Link}
                            href={contactInfo.socialMedia.youtube}
                            target="_blank"
                            color="primary"
                          >
                            <YouTubeIcon />
                          </IconButton>
                        )}
                      </Box>
                    )}
                  </Paper>
                </Box>
              </Grid>

              <Grid item xs={12} md={8}>
                {contactInfo?.mapUrl && (
                  <Box sx={{ mb: 4, height: '400px' }}>
                    <iframe
                      width="100%"
                      height="100%"
                      allowFullScreen
                      loading="lazy"
                      style={{ border: 0 }}
                      src={contactInfo?.mapUrl}
                      title="Gymkhana location map"
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                  </Box>
                )}
                <Paper
                  elevation={3}
                  sx={{
                    position: 'relative',
                    p: 4,
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.98) 100%)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.3)',
                    boxShadow: '0 8px 32px rgba(0, 31, 63, 0.1)',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'radial-gradient(circle at 0% 0%, rgba(0, 31, 63, 0.03) 0%, transparent 50%),\n                                radial-gradient(circle at 100% 100%, rgba(255, 64, 129, 0.03) 0%, transparent 50%)',
                      borderRadius: 'inherit',
                      zIndex: 0,
                    },
                    '& > *': {
                      position: 'relative',
                      zIndex: 1,
                    },
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      mb: 4,
                      color: 'primary.main',
                      fontWeight: 600,
                      textAlign: 'center',
                      position: 'relative',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: '-10px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '60px',
                        height: '3px',
                        background: 'linear-gradient(45deg, #1a237e 30%, #0d47a1 90%)',
                        borderRadius: '2px',
                      }
                    }}
                  >
                    Send us a Message
                  </Typography>
                  <Typography variant="body2" align="center" sx={{ mb: 3, color: 'text.secondary' }}>
                    Have a question or feedback? Fill out the form below and we’ll get back to you promptly.
                  </Typography>
                  <form onSubmit={handleSubmit}>
                    <Grid container spacing={3} wrap="wrap" columns={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }} alignItems="flex-start">
                      <Grid container spacing={3} wrap="wrap" columns={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }} alignItems="flex-start">


                        <TextField
                          fullWidth
                          label="Name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          variant="outlined"
                          inputProps={{ maxLength: 60 }}
                          helperText=" "
                          FormHelperTextProps={{ sx: { minHeight: 24 } }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <PersonOutlineIcon fontSize="small" />
                              </InputAdornment>
                            ),
                            sx: {
                              borderRadius: 2,
                              backgroundColor: 'rgba(255, 255, 255, 0.7)',
                              transition: 'all 0.3s ease-in-out',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 4px 8px rgba(0, 31, 63, 0.1)',
                              },
                            },
                          }}
                          InputLabelProps={{
                            sx: {
                              color: 'text.secondary',
                              '&.Mui-focused': {
                                color: 'primary.main',
                              },
                            },
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                borderWidth: '1px',
                                borderColor: 'rgba(0, 31, 63, 0.2)',
                              },
                              '&:hover fieldset': {
                                borderColor: 'primary.main',
                                borderWidth: '1px',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: 'primary.main',
                                borderWidth: '2px',
                              },
                            },
                          }}
                        />

                        <TextField
                          fullWidth
                          label="Email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          variant="outlined"
                          inputProps={{ maxLength: 100 }}
                          helperText=" "
                          FormHelperTextProps={{ sx: { minHeight: 24 } }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <EmailIcon fontSize="small" />
                              </InputAdornment>
                            ),
                            sx: {
                              borderRadius: 2,
                              backgroundColor: 'rgba(255, 255, 255, 0.7)',
                              transition: 'all 0.3s ease-in-out',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 4px 8px rgba(0, 31, 63, 0.1)',
                              },
                            },
                          }}
                          InputLabelProps={{
                            sx: {
                              color: 'text.secondary',
                              '&.Mui-focused': {
                                color: 'primary.main',
                              },
                            },
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                borderWidth: '1px',
                                borderColor: 'rgba(0, 31, 63, 0.2)',
                              },
                              '&:hover fieldset': {
                                borderColor: 'primary.main',
                                borderWidth: '1px',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: 'primary.main',
                                borderWidth: '2px',
                              },
                            },
                          }}
                        />
                      </Grid>

                      <Grid container spacing={3} wrap="wrap" columns={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }} alignItems="flex-start">


                        <TextField
                          fullWidth
                          label="Phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          variant="outlined"
                          inputProps={{ maxLength: 20 }}
                          helperText=" "
                          FormHelperTextProps={{ sx: { minHeight: 24 } }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <PhoneIcon fontSize="small" />
                              </InputAdornment>
                            ),
                            sx: {
                              borderRadius: 2,
                              backgroundColor: 'rgba(255, 255, 255, 0.7)',
                              transition: 'all 0.3s ease-in-out',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 4px 8px rgba(0, 31, 63, 0.1)',
                              },
                            },
                          }}
                          InputLabelProps={{
                            sx: {
                              color: 'text.secondary',
                              '&.Mui-focused': {
                                color: 'primary.main',
                              },
                            },
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                borderWidth: '1px',
                                borderColor: 'rgba(0, 31, 63, 0.2)',
                              },
                              '&:hover fieldset': {
                                borderColor: 'primary.main',
                                borderWidth: '1px',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: 'primary.main',
                                borderWidth: '2px',
                              },
                            },
                          }}
                        />


                        <TextField
                          fullWidth
                          label="Subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          required
                          variant="outlined"
                          inputProps={{ maxLength: SUBJECT_MAX }}
                          helperText={`${formData.subject.length}/${SUBJECT_MAX}`}
                          FormHelperTextProps={{ sx: { minHeight: 24 } }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SubjectIcon fontSize="small" />
                              </InputAdornment>
                            ),
                            sx: {
                              borderRadius: 2,
                              backgroundColor: 'rgba(255, 255, 255, 0.7)',
                              transition: 'all 0.3s ease-in-out',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 4px 8px rgba(0, 31, 63, 0.1)',
                              },
                            },
                          }}
                          InputLabelProps={{
                            sx: {
                              color: 'text.secondary',
                              '&.Mui-focused': {
                                color: 'primary.main',
                              },
                            },
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                borderWidth: '1px',
                                borderColor: 'rgba(0, 31, 63, 0.2)',
                              },
                              '&:hover fieldset': {
                                borderColor: 'primary.main',
                                borderWidth: '1px',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: 'primary.main',
                                borderWidth: '2px',
                              },
                            },
                          }}
                        />
                      </Grid>

                      <TextField
                        fullWidth
                        label="Message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        multiline
                        rows={6}
                        variant="outlined"
                        inputProps={{ maxLength: MESSAGE_MAX }}
                        helperText={`${formData.message.length}/${MESSAGE_MAX}`}
                        FormHelperTextProps={{ sx: { minHeight: 24 } }}
                        InputProps={{
                          sx: {
                            borderRadius: 2,
                            backgroundColor: 'rgba(255, 255, 255, 0.7)',
                            transition: 'all 0.3s ease-in-out',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              transform: 'translateY(-2px)',
                              boxShadow: '0 4px 8px rgba(0, 31, 63, 0.1)',
                            },
                          },
                        }}
                        InputLabelProps={{
                          sx: {
                            color: 'text.secondary',
                            '&.Mui-focused': {
                              color: 'primary.main',
                            },
                          },
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderWidth: '1px',
                              borderColor: 'rgba(0, 31, 63, 0.2)',
                            },
                            '&:hover fieldset': {
                              borderColor: 'primary.main',
                              borderWidth: '1px',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: 'primary.main',
                              borderWidth: '2px',
                            },
                          },
                        }}
                      />

                      <Grid item xs={12}>
                        <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          disabled={isDisabled}
                          startIcon={
                            loading ? (
                              <CircularProgress size={20} sx={{ color: 'white' }} />
                            ) : (
                              <SendIcon />
                            )
                          }
                          fullWidth
                          sx={{
                            mt: 2,
                            py: 1.8,
                            px: 4,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontSize: '1.1rem',
                            letterSpacing: '0.5px',
                            background: 'linear-gradient(45deg, #1a237e 30%, #0d47a1 90%)',
                            boxShadow: '0 3px 5px 2px rgba(26, 35, 126, 0.2)',
                            transition: 'all 0.3s ease-in-out',
                            '&:hover': {
                              background: 'linear-gradient(45deg, #0d47a1 30%, #1a237e 90%)',
                              transform: 'translateY(-2px)',
                              boxShadow: '0 6px 12px rgba(26, 35, 126, 0.3)',
                            },
                            '&:disabled': {
                              background: 'linear-gradient(45deg, #cccccc 30%, #999999 90%)',
                              boxShadow: 'none',
                            }
                          }}
                        >
                          {loading ? 'Sending...' : 'Send Message'}
                        </Button>
                      </Grid>
                    </Grid>
                  </form>
                </Paper>
              </Grid>
            </Grid>
          </motion.div>

          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert
              onClose={handleCloseSnackbar}
              severity={snackbar.severity}
              variant="filled"
              sx={{ width: '100%' }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Container>
      </Box>
    </Box>
  );
}

export default Contact;
